import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Menu, User, X, Search } from 'lucide-react';
import { useTheme } from './ThemeContext';
import { Logo } from './Logo';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { StockSearch } from './StockSearch';
import StockService from '../Api_Module/Api_Services/StockService';
import { useDispatch } from 'react-redux';
import { setCurrentSymbol, setMarketCurrentPrice, setMarketData } from '../store/AppSlice';
import { UserContext } from '../context/UserContext';
import { leftLinks as navLeftLinks, rightLinks as navRightLinks, toolLinks as navToolLinks, resourceLinks as navResourceLinks, companyLinks as navCompanyLinks, insightLinks as navInsightLinks } from './navigation/navConfig';

interface HeaderProps {
  // kept optional to match Layout usage and avoid TS error
  openPopup?: (signUpMode?: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ openPopup }) => {
  const headerRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { symbol } = useParams<{ symbol?: string }>();

  // Extract symbol only for routes like '/:symbol/:section' and ignore top-level pages
  const symbolFromRoute = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const denylist = new Set([
      'pricing',
      'about',
      'dashboard',
      'subscription',
      'login',
      'signup',
      'simulator',
      'compare',
      'profile',
      'settings',
      'institution',
      'hall-of-fame', // ✅ Added to prevent stock overview fetch
      'leaderboard'   // ✅ Added for nested routes
    ]);
    if (parts.length >= 2 && !denylist.has(parts[0])) {
      return parts[0];
    }
    return undefined;
  }, [location.pathname]);
  const effectiveSymbol = symbol ?? symbolFromRoute;

  const authToken = sessionStorage.getItem("STOCK_AUTH_TOKEN");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isMobileMenuOpen && !target.closest('.mobile-menu-container')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isMobileMenuOpen]);

  // Close search popover on outside click
  useEffect(() => {
    if (!isSearchOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('#header-search-popover') && !target.closest('#header-search-button')) {
        setIsSearchOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsSearchOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isSearchOpen]);

  const { theme } = useTheme();
  const dispatch = useDispatch();

  // Get user data from context
  const userContext = useContext(UserContext);
  if (!userContext) {
    throw new Error('Header must be used within a UserProvider');
  }
  const { userData } = userContext;
  // console.log('User Data in Header:', userData);

  const userInitials = useMemo(() => {
    const src = (userData?.name || (userData as any)?.email || '').trim();
    if (!src) return 'U';
    const parts = src.split(/\s+/);
    const first = parts[0]?.[0] || '';
    const second = parts[1]?.[0] || '';
    const letters = `${first}${second}`.toUpperCase();
    return letters || src.slice(0, 2).toUpperCase();
  }, [userData]);

  const getMarketData = async () => {
    try {
      if (effectiveSymbol) {
        // ✅ Guard: Skip for special routes
        if (effectiveSymbol === 'hall-of-fame' || effectiveSymbol === 'leaderboard') {
          console.log('⏭️ Skipping overview fetch for Hall of Fame route');
          return;
        }

        // ✅ Guard: Only fetch for valid stock symbols (1-5 uppercase letters)
        if (!/^[A-Z]{1,5}$/.test(effectiveSymbol)) {
          console.log('⏭️ Invalid stock symbol:', effectiveSymbol);
          return;
        }

        const response = await StockService.getOverview(effectiveSymbol);
        if (response) {
          dispatch(setMarketData(response));
          dispatch(setMarketCurrentPrice(response.price));
          dispatch(setCurrentSymbol({
            symbol: response.symbol,
            companyName: response.name,
            symbolImage: response.imageUrl
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching market data:', error);
    }
  };

  useEffect(() => {
    getMarketData();
    // getMarketData is stable (no deps) so exhaustive-deps is satisfied by including it
  }, [effectiveSymbol, getMarketData]);

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/");
    window.location.reload();
  }

  // Navigation utilities (kept minimal)

  // Dynamically set a CSS variable for header height so the hero can size to the remaining viewport
  useEffect(() => {
    const updateHeaderVar = () => {
      const h = headerRef.current?.offsetHeight || 80;
      document.documentElement.style.setProperty('--site-header-h', `${h}px`);
    };
    updateHeaderVar();
    window.addEventListener('resize', updateHeaderVar);
    return () => window.removeEventListener('resize', updateHeaderVar);
  }, []);

  // Nav config is centralized in navigation/navConfig.ts

  useEffect(() => {
    if (!toolsOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#tools-menu-button') && !target.closest('#tools-menu-popover')) {
        setToolsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [toolsOpen]);

  useEffect(() => {
    if (!insightsOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#insights-menu-button') && !target.closest('#insights-menu-popover')) {
        setInsightsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [insightsOpen]);

  useEffect(() => {
    if (!resourcesOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#resources-menu-button') && !target.closest('#resources-menu-popover')) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [resourcesOpen]);

  useEffect(() => {
    if (!profileMenuOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('#header-profile-menu')) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileMenuOpen]);

  return <header ref={headerRef} className={`relative z-20 border-b backdrop-blur ${theme === 'dark' ? 'bg-theme-primary/70 border-theme-secondary' : 'bg-white/70 border-gray-200'
    }`}>
    <div className="max-w-screen-2xl mx-auto px-3 py-1">
      {/* Unified flex row for laptop */}
      <div className="flex items-center gap-3">
        {/* Logo + mobile menu button (center logo on mobile) */}
        <div className="flex items-center w-full md:w-auto justify-between md:justify-start gap-2 shrink-0 relative">
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              className={`p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 md:hidden ${theme === 'dark'
                ? 'text-theme-secondary hover:text-theme-primary hover:bg-theme-secondary focus:ring-gray-600/30'
                : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:ring-gray-300/40'
                }`}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <Link to="/" className="hover:opacity-80 transition-opacity inline-flex">
              <Logo size="normal" variant="headerCompact" theme={theme} />
            </Link>
          </div>

          {/* Mobile Search Button */}
          <button
            onClick={() => setIsSearchOpen(v => !v)}
            className={`md:hidden p-2 rounded-md ${theme === 'dark' ? 'text-theme-secondary' : 'text-gray-700'}`}
          >
            <Search className="h-5 w-5" />
          </button>
        </div>

        {/* Centered navigation links for md+ screens */}
        <div className="hidden md:flex flex-1 justify-center">
          <nav className="flex items-center gap-1 xl:gap-2 flex-wrap">
            {navLeftLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={link.name === 'Dashboard' ? (e) => { e.preventDefault(); navigate('/'); } : undefined}
                className={`px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${(link.name === 'Dashboard' ? location.pathname === '/' : location.pathname.startsWith(link.path))
                  ? (theme === 'dark' ? 'bg-gray-800 text-white' : 'active-bg')
                  : (theme === 'dark' ? 'text-gray-300 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')
                  }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Tools dropdown */}
            <div className="relative" onMouseLeave={() => setToolsOpen(false)}>
              <button
                id="tools-menu-button"
                onClick={() => setToolsOpen(o => !o)}
                onMouseEnter={() => setToolsOpen(true)}
                aria-haspopup="true"
                aria-expanded={toolsOpen}
                className={`px-2.5 py-1.5 rounded-md text-[13px] font-medium flex items-center gap-1 transition-colors ${toolsOpen || navToolLinks.some(t => location.pathname.startsWith(t.path))
                  ? (theme === 'dark' ? 'bg-theme-secondary text-theme-primary' : 'active-bg text-gray-900')
                  : (theme === 'dark' ? 'text-theme-secondary hover:text-theme-primary hover:bg-theme-secondary' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')}`}
              >
                Tools
                <span className={`transition-transform text-xs ${toolsOpen ? 'rotate-180' : ''}`}>▾</span>
              </button>
              {toolsOpen && (
                <div
                  id="tools-menu-popover"
                  className={`absolute top-full left-0 mt-0.5 min-w-[180px] rounded-md border shadow-lg py-1 z-40 ${theme === 'dark' ? 'bg-theme-primary border-theme-tertiary' : 'bg-white border-gray-200'}`}
                  onMouseEnter={() => setToolsOpen(true)}
                  onMouseLeave={() => setToolsOpen(false)}
                >
                  {navToolLinks.map(l => (
                    <Link
                      key={l.path}
                      to={l.path}
                      onClick={() => setToolsOpen(false)}
                      className={`block px-3 py-2 text-[13px] whitespace-nowrap rounded-sm transition-colors ${location.pathname.startsWith(l.path)
                        ? (theme === 'dark' ? 'bg-theme-secondary text-theme-primary' : 'bg-gray-100 text-gray-900')
                        : (theme === 'dark' ? 'text-theme-secondary hover:bg-theme-secondary hover:text-theme-primary' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')}`}
                    >{l.name}</Link>
                  ))}
                </div>
              )}
            </div>

            {/* Insights dropdown */}
            <div className="relative" onMouseLeave={() => setInsightsOpen(false)}>
              <button
                id="insights-menu-button"
                onClick={() => setInsightsOpen(o => !o)}
                onMouseEnter={() => setInsightsOpen(true)}
                aria-haspopup="true"
                aria-expanded={insightsOpen}
                className={`px-2.5 py-1.5 rounded-md text-[13px] font-medium flex items-center gap-1 transition-colors ${insightsOpen || navInsightLinks.some(t => location.pathname.startsWith(t.path))
                  ? (theme === 'dark' ? 'bg-theme-secondary text-theme-primary' : 'active-bg text-gray-900')
                  : (theme === 'dark' ? 'text-theme-secondary hover:text-theme-primary hover:bg-theme-secondary' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')}`}
              >
                Insights
                <span className={`transition-transform text-xs ${insightsOpen ? 'rotate-180' : ''}`}>▾</span>
              </button>
              {insightsOpen && (
                <div
                  id="insights-menu-popover"
                  className={`absolute top-full left-0 mt-0.5 min-w-[180px] rounded-md border shadow-lg py-1 z-40 ${theme === 'dark' ? 'bg-theme-primary border-theme-tertiary' : 'bg-white border-gray-200'}`}
                  onMouseEnter={() => setInsightsOpen(true)}
                  onMouseLeave={() => setInsightsOpen(false)}
                >
                  {navInsightLinks.map(l => (
                    <Link
                      key={l.path}
                      to={l.path}
                      onClick={() => setInsightsOpen(false)}
                      className={`block px-3 py-2 text-[13px] whitespace-nowrap rounded-sm transition-colors ${location.pathname.startsWith(l.path)
                        ? (theme === 'dark' ? 'bg-theme-secondary text-theme-primary' : 'bg-gray-100 text-gray-900')
                        : (theme === 'dark' ? 'text-theme-secondary hover:bg-theme-secondary hover:text-theme-primary' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')}`}
                    >{l.name}</Link>
                  ))}
                </div>
              )}
            </div>

            {/* Resources dropdown - temporarily disabled
            <div className="relative" onMouseLeave={() => setResourcesOpen(false)}>
              <button
                id="resources-menu-button"
                onClick={() => setResourcesOpen(o => !o)}
                onMouseEnter={() => setResourcesOpen(true)}
                aria-haspopup="true"
                aria-expanded={resourcesOpen}
                className={`px-2.5 py-1.5 rounded-md text-[13px] font-medium flex items-center gap-1 transition-colors ${resourcesOpen || navResourceLinks.some(t => location.pathname.startsWith(t.path))
                  ? (theme === 'dark' ? 'bg-theme-secondary text-theme-primary' : 'bg-gray-200 text-gray-900')
                  : (theme === 'dark' ? 'text-theme-secondary hover:text-theme-primary hover:bg-theme-secondary' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')}`}
              >
                Resources
                <span className={`transition-transform text-xs ${resourcesOpen ? 'rotate-180' : ''}`}>▾</span>
              </button>
              {resourcesOpen && (
                <div
                  id="resources-menu-popover"
                  className={`absolute top-full left-0 mt-0.5 min-w-[180px] rounded-md border shadow-lg py-1 z-40 ${theme === 'dark' ? 'bg-theme-primary border-theme-tertiary' : 'bg-white border-gray-200'}`}
                  onMouseEnter={() => setResourcesOpen(true)}
                  onMouseLeave={() => setResourcesOpen(false)}
                >
                  {(() => {
                    const exclude = new Set<string>([
                      ...navLeftLinks.map(x => x.name),
                      ...navRightLinks.map(x => x.name),
                      ...navToolLinks.map(x => x.name),
                    ]);
                    return navResourceLinks
                      .filter(l => !exclude.has(l.name))
                      .map(l => (
                        <Link
                          key={l.path}
                          to={l.path}
                          onClick={() => setResourcesOpen(false)}
                          className={`block px-3 py-2 text-[13px] whitespace-nowrap rounded-sm transition-colors ${location.pathname.startsWith(l.path)
                            ? (theme === 'dark' ? 'bg-theme-secondary text-theme-primary' : 'bg-gray-100 text-gray-900')
                            : (theme === 'dark' ? 'text-theme-secondary hover:bg-theme-secondary hover:text-theme-primary' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900')}`}
                        >{l.name}</Link>
                      ));
                  })()}
                </div>
              )}
            </div>
            */}

            {navRightLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={link.name === 'Dashboard' ? (e) => { e.preventDefault(); navigate('/'); } : undefined}
                className={`px-2.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${(link.name === 'Dashboard' ? location.pathname === '/' : location.pathname.startsWith(link.path))
                  ? (theme === 'dark' ? 'bg-theme-secondary text-theme-primary' : ' active-bg text-gray-900')
                  : (theme === 'dark' ? 'text-theme-secondary hover:text-theme-primary hover:bg-theme-secondary' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100')
                  }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>
        </div>

        {/* Search (grow to take remaining space) and actions */}
        <div className="hidden md:flex items-center gap-2 ml-auto">
          <div className="w-[240px] lg:w-[300px] xl:w-[340px]">
            <StockSearch
              placeholder="Search for a stock symbol..."
              onSelect={(sym) => navigate(`/${sym}/summary`)}
            />
          </div>
          {authToken ? (
            <>
              {/* Subscription pill retained */}
              <div className="hidden lg:flex items-center space-x-2 mr-1">
                {((userData?.subscriptionStatus === 'trialing' && userData?.planActive && userData?.isTrial) ||
                  (userData?.subscriptionStatus === 'expired') ||
                  (userData?.subscriptionStatus === 'active' && userData?.planActive)) && (
                    <div className={`text-[11px] px-2.5 py-1 rounded-full border font-medium ${userData?.subscriptionStatus === 'expired' || (userData?.daysLeft || 0) <= 0
                      ? 'border-red-300 text-red-500 dark:border-red-600 dark:text-red-400'
                      : userData?.subscriptionStatus === 'active'
                        ? 'border-green-300 text-green-600 dark:border-green-600 dark:text-green-400'
                        : (userData?.daysLeft || 0) <= 3
                          ? 'border-orange-300 text-orange-600 dark:border-orange-600 dark:text-orange-400'
                          : 'active-border active-bg'
                      }`}>
                      {userData?.subscriptionStatus === 'expired'
                        ? 'Expired'
                        : userData?.subscriptionStatus === 'active'
                          ? `${userData?.planInterval === 'month' ? 'Monthly' : 'Yearly'} plan`
                          : (userData?.daysLeft || 0) <= 0
                            ? 'Trial ended'
                            : `${userData?.daysLeft} trial day${(userData?.daysLeft || 0) !== 1 ? 's' : ''}`}
                    </div>
                  )}
              </div>
              {/* Search button hidden on desktop as requested */}
              {/* <button
                id="header-search-button"
                onClick={() => setIsSearchOpen(v => !v)}
                aria-label={isSearchOpen ? 'Close search' : 'Open search'}
                className={`hidden md:block p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-0 ${theme === 'dark'
                  ? 'text-theme-secondary hover:text-theme-primary hover:bg-theme-secondary focus:ring-gray-600/30'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:ring-gray-300/40'
                  }`}
              >
                <Search className="h-5 w-5" />
              </button>

              {/* Profile Menu */}
              <div className="relative" id="header-profile-menu">
                <button
                  onClick={() => setProfileMenuOpen(o => !o)}
                  aria-haspopup="true"
                  aria-expanded={profileMenuOpen}
                  className={`p-0.5 rounded-full border outline-none active-border`}
                >
                  <div className="w-8 h-8 rounded-full active-bg text-white flex items-center justify-center text-xs font-bold">
                    {userInitials}
                  </div>
                </button>
                {profileMenuOpen && (
                  <div className={`absolute right-0 top-full mt-2 w-44 rounded-md border shadow-lg z-50 overflow-hidden ${theme === 'dark' ? 'bg-theme-primary border-theme-tertiary' : 'bg-white border-gray-200'}`}>
                    <Link
                      to="/profile"
                      className={`block px-3 py-2 text-sm ${theme === 'dark' ? 'text-theme-primary hover:bg-theme-secondary' : 'text-gray-900 hover:bg-gray-100'}`}
                      onClick={() => setProfileMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <button
                      onClick={() => { setProfileMenuOpen(false); handleLogout(); }}
                      className={`w-full text-left px-3 py-2 text-sm ${theme === 'dark' ? 'text-red-400 hover:bg-theme-secondary' : 'text-red-600 hover:bg-gray-100'}`}
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
              {isSearchOpen && (
                <div
                  id="header-search-popover"
                  className={`absolute right-2 top-full mt-2 w-[min(92vw,420px)] p-3 rounded-lg border shadow-lg z-50 ${theme === 'dark' ? 'bg-theme-primary border-theme-tertiary' : 'bg-white border-gray-200'}`}
                >
                  <StockSearch
                    placeholder="Search stocks..."
                    className="w-full"
                    autoFocus={true}
                    onSelect={(sym) => { navigate(`/${sym}/summary`); setIsSearchOpen(false); }}
                  />
                </div>
              )}
            </>
          ) : (
            // Compact auth for laptop: single link
            <button
              onClick={() => { if (openPopup) openPopup(); else navigate('/login'); }}
              className={`btn active hidden md:inline-flex px-3 py-1.5 rounded-md text-xs font-semibold transition-colors '}`}
            >
              Login / Signup
            </button>
          )}
        </div>
      </div>
    </div>

    {/* Mobile drawer remains */}
    {createPortal(
      <div className={`mobile-menu-container fixed inset-0 z-50 ${isMobileMenuOpen ? '' : 'pointer-events-none'}`}>
        {/* Backdrop */}
        <div
          className={`fixed inset-0 transition-opacity ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0'} ${theme === 'dark' ? 'bg-black/60' : 'bg-black/40'}`}
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
        {/* Panel */}
        <aside
          className={`fixed left-0 top-0 h-full w-[85vw] max-w-sm transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            } ${theme === 'dark' ? 'bg-theme-primary' : 'bg-white'} border-r ${theme === 'dark' ? 'border-theme-secondary' : 'border-gray-200'} shadow-xl overflow-y-auto`}
        >
          <div className="px-4 pt-5 pb-4 border-b border-theme-tertiary flex items-center gap-3">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
              className={`p-2 rounded-md ${theme === 'dark' ? 'text-theme-secondary hover:text-theme-primary hover:bg-theme-secondary' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
            >
              <X className="h-6 w-6" />
            </button>
            <div className="shrink-0 flex items-center gap-2">
              <img
                src={`logo.png`}
                alt="Stock Fortress Logo"
                className="h-4 sm:h-5 md:h-6 lg:h-7 xl:h-8 w-auto block"
              />
            </div>
          </div>

          <div className="p-4 space-y-2">
            {/* Sections */}
            <div className="text-xs font-semibold uppercase tracking-wide text-theme-muted px-1">Account</div>
            {!authToken ? (
              <div className="space-y-1">
                <button
                  onClick={() => {
                    if (openPopup) openPopup(); else navigate('/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-theme-tertiary text-theme-primary"
                >
                  Log In / Sign Up
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                <Link
                  to="/profile"
                  className="flex items-center px-3 py-2 rounded-md hover:bg-theme-tertiary text-theme-primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <User className="h-5 w-5 mr-2 text-theme-muted" /> Your Profile
                </Link>
                <button
                  onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-red-50 text-red-600"
                >
                  Log out
                </button>
              </div>
            )}

            <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-theme-muted px-1">Navigation</div>
            <div className="space-y-1">
              {[...navLeftLinks, ...navRightLinks.filter(r => r.name !== 'Pricing' && r.name !== 'About')].map(l => (
                <Link
                  key={l.path}
                  to={l.path}
                  className="block px-3 py-2 rounded-md hover:bg-theme-tertiary text-theme-primary"
                  onClick={(e) => { if (l.name === 'Dashboard') { e.preventDefault(); navigate('/'); } setIsMobileMenuOpen(false); }}
                >
                  {l.name}
                </Link>
              ))}
            </div>

            <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-theme-muted px-1">Tools</div>
            <div className="space-y-1">
              {navToolLinks.map(l => (
                <Link
                  key={l.path}
                  to={l.path}
                  className="block px-3 py-2 rounded-md hover:bg-theme-tertiary text-theme-primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {l.name}
                </Link>
              ))}
            </div>

            <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-theme-muted px-1">Insights</div>
            <div className="space-y-1">
              {navInsightLinks.map(l => (
                <Link
                  key={l.path}
                  to={l.path}
                  className="block px-3 py-2 rounded-md hover:bg-theme-tertiary text-theme-primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {l.name}
                </Link>
              ))}
            </div>

            {/* Resources - temporarily disabled
            <div className="mt-4 text-xs font-semibold uppercase tracking-wide text-theme-muted px-1">Resources</div>
            <div className="space-y-1">
              {(() => {
                const exclude = new Set<string>([
                  ...navLeftLinks.map(x => x.name),
                  ...navRightLinks.map(x => x.name),
                  ...navToolLinks.map(x => x.name),
                ]);
                return navResourceLinks
                  .filter(l => !exclude.has(l.name))
                  .map(l => (
                    <Link
                      key={l.path}
                      to={l.path}
                      className="block px-3 py-2 rounded-md hover:bg-theme-tertiary text-theme-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {l.name}
                    </Link>
                  ));
              })()}
            </div>
            */}

            <div className="mt-6 text-xs font-semibold uppercase tracking-wide text-theme-muted px-1">Company</div>
            <div className="space-y-1">
              {navCompanyLinks.map(l => (
                <Link
                  key={l.path}
                  to={l.path}
                  className="block px-3 py-2 rounded-md hover:bg-theme-tertiary text-theme-primary"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {l.name}
                </Link>
              ))}
            </div>

            {/* CTA (only when logged out) */}
            {!authToken && (
              <div className="pt-4 mt-6 border-t border-theme-tertiary">
                <button
                  onClick={() => { if (openPopup) openPopup(true); else navigate('/signup'); setIsMobileMenuOpen(false); }}
                  className={`w-full px-4 py-3 rounded-full font-semibold tracking-wide transition-colors ${theme === 'dark'
                    ? 'bg-indigo-900 text-theme-primary hover:bg-indigo-800'
                    : 'bg-indigo-600 text-theme-primary hover:bg-indigo-700'
                    }`}
                >
                  GET STARTED, IT'S FREE
                </button>
              </div>
            )}
          </div>
        </aside>
      </div>,
      document.body
    )}
  </header>;
};

export default Header;
